import { Injectable } from '@angular/core';
import { Mode } from '../models/mode.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { Shop } from '../models/shop.model';
import { BulkWorkOrderHeaderValue } from '../models/bulk-work-order/bulk-work-order-header.model';
import { BulkWorkOrderRepairLineItem } from '../models/bulk-work-order/bulk-work-order-repair-line-item.model';
import { MaintenanceOrderManHours } from '../models/maintenance-order-man-hours';
import { FormControlStatus } from '@angular/forms';
import { Equipment } from '../models/equipment.model';
import { EquipmentPtiStatus } from '../models/equipment-pti-status.model';
import { EquipmentAlarm } from '../models/equipment-alarm.model';
import { BulkWorkOrderContainerSizeError } from '../models/bulk-work-order/bulk-work-order-container-size-error.model';
import { BulkWorkOrderManufacturerError } from '../models/bulk-work-order/bulk-work-order-manufacturer-error.model';
import { utils } from '../helper/utils';

@Injectable({
  providedIn: 'root',
})
export class BulkWorkOrderSharedDataService {
  private reset$ = new BehaviorSubject<boolean>(false);
  get Reset(): Observable<boolean> {
    return this.reset$.asObservable();
  }
  set Reset(value: boolean) {
    this.reset$.next(value);
  }

  private mode$ = new BehaviorSubject<Mode | null>(null);
  get Mode(): Observable<Mode | null> {
    return this.mode$.asObservable();
  }
  set Mode(mode: Mode | null) {
    this.mode$.next(mode);
  }

  private shop$ = new BehaviorSubject<Shop | null>(null);
  get Shop(): Observable<Shop | null> {
    return this.shop$.asObservable();
  }
  set Shop(shop: Shop | null) {
    this.shop$.next(shop);
  }

  private validatedEquipments$ = new BehaviorSubject<Equipment[]>([]);
  get ValidatedEquipments(): Observable<Equipment[]> {
    return this.validatedEquipments$.asObservable();
  }
  set AddValidatedEquipment(equipments: Equipment) {
    const currentEquipments = this.validatedEquipments$.value;
    this.validatedEquipments$.next([...currentEquipments, equipments]);
  }

  ClearValidatedEquipments() {
    this.validatedEquipments$.next([]);
  }

  get ValdatedEquipmentsSnapShot(): Equipment[] {
    return this.validatedEquipments$.value;
  }

  get equipmentSize(): number | undefined {
    return this.validatedEquipments$.value[0]?.equipmentSize;
  }

  private bulkWorkOrderHeader$ = new BehaviorSubject<BulkWorkOrderHeaderValue>({} as BulkWorkOrderHeaderValue);
  get BulkWorkOrderHeader(): Observable<BulkWorkOrderHeaderValue> {
    return this.bulkWorkOrderHeader$.asObservable();
  }
  set BulkWorkOrderHeader(bulkWorkOrderHeader: BulkWorkOrderHeaderValue) {
    this.bulkWorkOrderHeader$.next(bulkWorkOrderHeader);
  }

  private bulkWorkOrderRepairLineItems$ = new BehaviorSubject<BulkWorkOrderRepairLineItem[]>([]);
  get BulkWorkOrderRepairLineItems(): Observable<BulkWorkOrderRepairLineItem[]> {
    return this.bulkWorkOrderRepairLineItems$.asObservable();
  }
  set BulkWorkOrderRepairLineItems(bulkWorkOrderRepairLineItems: BulkWorkOrderRepairLineItem[]) {
    this.bulkWorkOrderRepairLineItems$.next(bulkWorkOrderRepairLineItems);
  }

  private bulkWorkOrderManHours$ = new BehaviorSubject<MaintenanceOrderManHours[]>([]);
  get BulkWorkOrderManHours(): Observable<MaintenanceOrderManHours[]> {
    return this.bulkWorkOrderManHours$.asObservable();
  }
  set BulkWorkOrderManHours(bulkWorkOrderManHours: MaintenanceOrderManHours[]) {
    this.bulkWorkOrderManHours$.next(bulkWorkOrderManHours);
  }

  private bulkWorkOrderRemark$ = new BehaviorSubject<string>('');
  get BulkWorkOrderRemark(): Observable<string> {
    return this.bulkWorkOrderRemark$.asObservable();
  }
  set BulkWorkOrderRemark(bulkWorkOrderRemark: string) {
    this.bulkWorkOrderRemark$.next(bulkWorkOrderRemark);
  }

  private headerFormStatus$ = new BehaviorSubject<FormControlStatus>('INVALID');
  get HeaderFormStatus(): Observable<FormControlStatus> {
    return this.headerFormStatus$.asObservable();
  }
  set HeaderFormStatus(status: FormControlStatus) {
    this.headerFormStatus$.next(status);
  }

  private repairLineItemFormStatus$ = new BehaviorSubject<FormControlStatus>('INVALID');
  get RepairLineItemFormStatus(): Observable<FormControlStatus> {
    return this.repairLineItemFormStatus$.asObservable();
  }
  set RepairLineItemFormStatus(status: FormControlStatus) {
    this.repairLineItemFormStatus$.next(status);
  }

  private manHoursFormStatus$ = new BehaviorSubject<FormControlStatus>('INVALID');
  get ManHoursFormStatus(): Observable<FormControlStatus> {
    return this.manHoursFormStatus$.asObservable();
  }
  set ManHoursFormStatus(status: FormControlStatus) {
    this.manHoursFormStatus$.next(status);
  }

  private containerGradeValidationErrors = new Map<string, string[]>();
  private containerGradeValidationErrors$ = new BehaviorSubject<string[]>([]);
  get ContainerGradeValidationErrors(): Observable<string[]> {
    return this.containerGradeValidationErrors$.asObservable();
  }
  set ContainerGradeValidationErrors(error: Map<string, string[]>) {
    this.containerGradeValidationErrors = error;
    let totalError: string[] = [];
    this.containerGradeValidationErrors.forEach((value, key) => {
      let errors = value.map((err) => `Repair Code: ${key}, Container: ${err} cannot be upgraded to food grade`);
      totalError = totalError.concat(errors);
    });
    this.containerGradeValidationErrors$.next(totalError);
  }

  ClearContainerGradeValidationError(repairCode: string) {
    let totalError: string[] = [];
    this.containerGradeValidationErrors.set(repairCode, []);
    this.containerGradeValidationErrors.forEach((value, key) => {
      let errors = value.map((err) => `Repair Code: ${key}, Container: ${err} cannot be upgraded to food grade`);
      totalError = totalError.concat(errors);
    });
    this.containerGradeValidationErrors$.next(totalError);
  }

  private containerSizeValidationErrors: BulkWorkOrderContainerSizeError = {
    errorEquipments: new Map<string, Equipment[]>(),
    validSizes: new Map<string, number[]>(),
  };

  private containerSizeValidationErrors$ = new BehaviorSubject<string[]>([]);
  get ContainerSizeValidationErrors(): Observable<string[]> {
    return this.containerSizeValidationErrors$.asObservable();
  }
  set ContainerSizeValidationErrors(error: BulkWorkOrderContainerSizeError) {
    this.containerSizeValidationErrors = error;
    let totalError: string[] = [];

    this.containerSizeValidationErrors.errorEquipments.forEach((value, key) => {
      if (value.length > 0) {
        const validSizeListWithFt = (this.containerSizeValidationErrors.validSizes.get(key) || []).map((size) => `${size} ft`);
        const validSizesFormatted = utils.formatStringList(validSizeListWithFt);

        const equipmentNumbers = utils.formatStringList(value.map((e) => e.equipmentNumber ?? ''));

        const errorMessage = `Repair code ${key} is valid for ${validSizesFormatted} containers only. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`;

        totalError.push(errorMessage);
      }
    });
    this.containerSizeValidationErrors$.next(totalError);
  }

  ClearContainerSizeValidationError(repairCode: string) {
    let totalError: string[] = [];
    if (this.containerSizeValidationErrors.errorEquipments.has(repairCode)) {
      this.containerSizeValidationErrors.errorEquipments.set(repairCode, []);
    }

    this.containerSizeValidationErrors.errorEquipments.forEach((value, key) => {
      if (value.length > 0) {
        const validSizeListWithFt = (this.containerSizeValidationErrors.validSizes.get(key) || []).map((size) => `${size} ft`);
        const validSizesFormatted = utils.formatStringList(validSizeListWithFt);
        const equipmentNumbers = utils.formatStringList(value.map((e) => e.equipmentNumber ?? ''));
        const errorMessage = `Repair code ${key} is valid for ${validSizesFormatted} containers only. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`;
        totalError.push(errorMessage);
      }
    });

    this.containerSizeValidationErrors$.next(totalError);
  }

  private manufacturerValidationErrors: BulkWorkOrderManufacturerError = {
    errorEquipments: new Map<string, Equipment[]>(),
    hasMixedManufacturers: false,
  };

  private readonly manufacturerValidationErrors$ = new BehaviorSubject<string[]>([]);
  get ManufacturerValidationErrors(): Observable<string[]> {
    return this.manufacturerValidationErrors$.asObservable();
  }
  set ManufacturerValidationErrors(error: BulkWorkOrderManufacturerError) {
    this.manufacturerValidationErrors = error;
    const totalError: string[] = [];

    this.manufacturerValidationErrors.errorEquipments.forEach((value, key) => {
      if (value.length > 0) {
        const equipmentNumbers = utils.formatStringList(value.map((e) => e.equipmentNumber ?? ''));
        const errorMessage = this.manufacturerValidationErrors.hasMixedManufacturers
          ? `Repair code ${key} is not valid when different reefer manufacturers are present. Only repair codes starting with '0' are allowed. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`
          : `Repair code ${key} is not valid for the reefer unit manufacturer of the selected containers. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`;
        totalError.push(errorMessage);
      }
    });
    this.manufacturerValidationErrors$.next(totalError);
  }

  ClearManufacturerValidationError(repairCode: string) {
    const totalError: string[] = [];
    if (this.manufacturerValidationErrors.errorEquipments.has(repairCode)) {
      this.manufacturerValidationErrors.errorEquipments.set(repairCode, []);
    }

    this.manufacturerValidationErrors.errorEquipments.forEach((value, key) => {
      if (value.length > 0) {
        const equipmentNumbers = utils.formatStringList(value.map((e) => e.equipmentNumber ?? ''));
        const errorMessage = this.manufacturerValidationErrors.hasMixedManufacturers
          ? `Repair code ${key} is not valid when different reefer manufacturers are present. Only repair codes starting with '0' are allowed. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`
          : `Repair code ${key} is not valid for the reefer unit manufacturer of the selected containers. Please change the repair code or remove inapplicable containers (${equipmentNumbers}) from the list.`;
        totalError.push(errorMessage);
      }
    });
    this.manufacturerValidationErrors$.next(totalError);
  }

  private ptiStatus$ = new BehaviorSubject<(EquipmentPtiStatus & { equipmentNumber: string })[] | undefined>(undefined);
  get PtiStatus(): Observable<(EquipmentPtiStatus & { equipmentNumber: string })[] | undefined> {
    return this.ptiStatus$.asObservable();
  }
  public AddPtiStatus(pti: EquipmentPtiStatus & { equipmentNumber: string }) {
    const existing = this.ptiStatus$.value;
    if (existing) {
      this.ptiStatus$.next([...existing, pti]);
    }
  }

  private pastRepairCodes$ = new BehaviorSubject<{ equipmentNumber: string; repairCodes: string[] }[] | undefined>(undefined);
  get PastRepairCodes(): Observable<{ equipmentNumber: string; repairCodes: string[] }[] | undefined> {
    return this.pastRepairCodes$.asObservable();
  }

  public AddPastRepairCodes(repairCodes: string[], equipmentNumber: string) {
    const existing = this.pastRepairCodes$.value;
    if (existing) {
      this.pastRepairCodes$.next([...existing, { equipmentNumber: equipmentNumber, repairCodes: repairCodes }]);
    }
  }

  private alarms$ = new BehaviorSubject<(EquipmentAlarm & { equipmentNumber: string })[] | undefined>(undefined);
  get Alarms(): Observable<(EquipmentAlarm & { equipmentNumber: string })[] | undefined> {
    return this.alarms$.asObservable();
  }

  public AddAlarms(alarm: EquipmentAlarm & { equipmentNumber: string }) {
    const existing = this.alarms$.value;
    if (existing) {
      this.alarms$.next([...existing, alarm]);
    }
  }
}
